import { createContext, use } from 'react'

/**
 * A slot among the Add tiles of a course's Activities section, on Create Course and Edit Course
 * alike, where other areas add their own way of adding activities, such as Launch's Add from Tool
 * (LCH-18). `recordId` is the saved course's id when the form saves to that course, and absent on a
 * course not saved yet, one started from a template and a Save As copy. A contribution renders
 * `ActivityAddTile`, so it looks like the tiles beside it, and adds through `useActivityAdding`.
 */
export const activityAddSlot = 'catalog.activity-editor.add'

/** One LTI link a contribution adds to the form, as an LTI link activity. */
export interface AddedLtiLink {
  readonly name: string
  readonly description: string | null
  readonly targetLinkUri: string | null
  readonly customParameters: Readonly<Record<string, string>>
}

/** LTI links one tool returned, with the tool's public id (`ltt_…`). */
export interface LtiLinksToAdd {
  readonly toolId: string
  readonly links: readonly AddedLtiLink[]
}

/** What a contribution to the Add slot may ask of the course form. */
export interface ActivityAdding {
  /**
   * Loads LTI links with `load` and adds them to the form's activities as unsaved LTI link
   * activities, like any other added activity: the author's save keeps them. Save waits while they
   * load. Resolves to how many were added, and rejects when `load` does. While the form is saving
   * it calls nothing and resolves `null`: the links stay where they are, to be offered again once
   * the save is over. The property is `null` where the form cannot take links at all.
   */
  readonly addLtiLinks: ((load: () => Promise<LtiLinksToAdd>) => Promise<number | null>) | null
}

export const ActivityAddingContext = createContext<ActivityAdding>({ addLtiLinks: null })

/** What the surrounding course form lets an Add slot contribution do. */
export function useActivityAdding(): ActivityAdding {
  return use(ActivityAddingContext)
}
